export const GLOBAL_ROLES = [
  "super_admin",
  "accounting",
  "admin",
  "sales",
  "outreach",
  "investor",
  "external_user",
] as const;

export type GlobalRole = (typeof GLOBAL_ROLES)[number];

export const DEFAULT_GLOBAL_ROLE: GlobalRole = "external_user";

export const ADMIN_ROLES = [
  "super_admin",
  "accounting",
  "admin",
  "sales",
] as const satisfies ReadonlyArray<GlobalRole>;

export type AdminRole = (typeof ADMIN_ROLES)[number];

const ADMIN_ROLE_SET = new Set<string>(ADMIN_ROLES);
export function isAdminRole(role: string | undefined): role is AdminRole {
  return role !== undefined && ADMIN_ROLE_SET.has(role);
}

export const ACCOUNTING_ROLES = [
  "super_admin",
  "accounting",
] as const satisfies ReadonlyArray<GlobalRole>;

export type AccountingRole = (typeof ACCOUNTING_ROLES)[number];

const ACCOUNTING_ROLE_SET = new Set<string>(ACCOUNTING_ROLES);
export function isAccountingRole(
  role: string | undefined,
): role is AccountingRole {
  return role !== undefined && ACCOUNTING_ROLE_SET.has(role);
}

export const SUPER_ADMIN_ROLES = [
  "super_admin",
] as const satisfies ReadonlyArray<GlobalRole>;

export type SuperAdminRole = (typeof SUPER_ADMIN_ROLES)[number];

const SUPER_ADMIN_ROLE_SET = new Set<string>(SUPER_ADMIN_ROLES);
export function isSuperAdminRole(
  role: string | undefined,
): role is SuperAdminRole {
  return role !== undefined && SUPER_ADMIN_ROLE_SET.has(role);
}

export const ACCEPT_FOR_CUSTOMER_ROLES = [
  "super_admin",
  "admin",
  "sales",
] as const satisfies ReadonlyArray<GlobalRole>;

const ACCEPT_FOR_CUSTOMER_ROLE_SET = new Set<string>(ACCEPT_FOR_CUSTOMER_ROLES);
export function isAcceptForCustomerRole(role: string | undefined): boolean {
  return role !== undefined && ACCEPT_FOR_CUSTOMER_ROLE_SET.has(role);
}

export const ADMIN_TIER_ROLES = [
  "super_admin",
  "accounting",
  "admin",
] as const satisfies ReadonlyArray<GlobalRole>;

export type AdminTierRole = (typeof ADMIN_TIER_ROLES)[number];

const ADMIN_TIER_ROLE_SET = new Set<string>(ADMIN_TIER_ROLES);
export function isAdminTierRole(
  role: string | undefined,
): role is AdminTierRole {
  return role !== undefined && ADMIN_TIER_ROLE_SET.has(role);
}

export const OUTREACH_ROLES = [
  "super_admin",
  "accounting",
  "admin",
  "sales",
  "outreach",
] as const satisfies ReadonlyArray<GlobalRole>;

export type OutreachRole = (typeof OUTREACH_ROLES)[number];

const OUTREACH_ROLE_SET = new Set<string>(OUTREACH_ROLES);
export function isOutreachRole(role: string | undefined): role is OutreachRole {
  return role !== undefined && OUTREACH_ROLE_SET.has(role);
}

export function isOutreachOnlyRole(role: string | undefined): boolean {
  return role === "outreach";
}

export const INVESTOR_DASHBOARD_ROLES = [
  "super_admin",
  "accounting",
  "admin",
  "investor",
] as const satisfies ReadonlyArray<GlobalRole>;

export type InvestorDashboardRole = (typeof INVESTOR_DASHBOARD_ROLES)[number];

const INVESTOR_DASHBOARD_ROLE_SET = new Set<string>(INVESTOR_DASHBOARD_ROLES);
export function isInvestorDashboardRole(
  role: string | undefined,
): role is InvestorDashboardRole {
  return role !== undefined && INVESTOR_DASHBOARD_ROLE_SET.has(role);
}

export function isInvestorOnlyRole(role: string | undefined): boolean {
  return role === "investor";
}

export function holdsStaffRole(role: string | undefined): boolean {
  return isOutreachRole(role) || isInvestorDashboardRole(role);
}

export const OPERATIONAL_ROLES = [
  "admin",
  "sales",
] as const satisfies ReadonlyArray<GlobalRole>;

export const GLOBAL_ROLE_LABELS: Record<GlobalRole, string> = {
  super_admin: "Super admin",
  accounting: "Accounting",
  admin: "Admin",
  sales: "Sales",
  outreach: "Outreach",
  investor: "Investor",
  external_user: "External user",
};

export const GLOBAL_ROLE_OPTIONS: Array<{
  value: GlobalRole;
  label: string;
}> = GLOBAL_ROLES.map((value) => ({
  value,
  label: GLOBAL_ROLE_LABELS[value],
}));

export const GLOBAL_ROLE_DESCRIPTIONS: Record<GlobalRole, string> = {
  super_admin: "Full access, including billing and admin role management.",
  accounting: "Full access to billing, invoices, statements, and financials.",
  admin:
    "Operational admin: sees and edits every order's pricing, costs, margin, freight and paperwork. No Billing pages, money movement or credit decisions.",
  sales:
    "Operational admin for sales and order workflows, including each order's pricing, costs and margin. No Billing pages, money movement or credit decisions.",
  outreach:
    "Outreach queue and the accounts behind it, including raising an account with its first listings. No orders, quotes, or financials.",
  investor:
    "The investor dashboard only. Read-only company metrics; no orders, accounts, or customer data.",
  external_user: "Default marketplace user without admin access.",
};

export const ORG_ROLES = {
  OWNER: "owner",
  ADMIN: "admin",
  MEMBER: "member",
  SALES: "sales",
  WAREHOUSE: "warehouse",
  ACCOUNTING: "accounting",
  READ_ONLY: "read_only",
} as const;

export type OrgRole = (typeof ORG_ROLES)[keyof typeof ORG_ROLES];

export const ORG_ROLE_LABELS: Record<OrgRole, string> = {
  owner: "Owner",
  admin: "Admin",
  member: "Member",
  sales: "Sales",
  warehouse: "Warehouse",
  accounting: "Accounting",
  read_only: "Read-only",
};

export const ORG_ROLE_DESCRIPTIONS: Record<OrgRole, string> = {
  owner: "Primary company owner with full access.",
  admin: "Can manage members, settings, and company access.",
  member: "Can view company activity and collaborate with the team.",
  sales: "Can manage sales workflows, quotes, and customer activity.",
  warehouse: "Can manage inventory and location operations.",
  accounting: "Can review billing, invoices, statements, and exports.",
  read_only: "Can view company data without making operational changes.",
};

const ORG_ADMIN_ROLE_SET = new Set<string>([ORG_ROLES.OWNER, ORG_ROLES.ADMIN]);

export function isOrgAdmin(role: string | undefined): boolean {
  return role !== undefined && ORG_ADMIN_ROLE_SET.has(role);
}

const ORG_BILLING_MANAGER_ROLE_SET = new Set<string>([
  ORG_ROLES.OWNER,
  ORG_ROLES.ADMIN,
  ORG_ROLES.ACCOUNTING,
]);

export function isOrgBillingManager(role: string | undefined): boolean {
  return role !== undefined && ORG_BILLING_MANAGER_ROLE_SET.has(role);
}

export function canWriteForCompany(
  globalRole: string | undefined,
  membershipRole: string | undefined,
): boolean {
  return (
    isAdminRole(globalRole) ||
    (membershipRole !== undefined && membershipRole !== ORG_ROLES.READ_ONLY)
  );
}

export function canChangeCompanyEntityType(
  globalRole: string | undefined,
  membershipRole: string | undefined,
): boolean {
  return isAdminRole(globalRole) || isOrgAdmin(membershipRole);
}

export const ASSIGNABLE_ORG_ROLES = [
  ORG_ROLES.ADMIN,
  ORG_ROLES.MEMBER,
] as const satisfies ReadonlyArray<OrgRole>;

export type AssignableOrgRole = (typeof ASSIGNABLE_ORG_ROLES)[number];

const ASSIGNABLE_ORG_ROLE_SET = new Set<string>(ASSIGNABLE_ORG_ROLES);

export function isAssignableOrgRole(
  role: string | undefined,
): role is AssignableOrgRole {
  return role !== undefined && ASSIGNABLE_ORG_ROLE_SET.has(role);
}

export function normalizeWorkosRoleSlug(
  slug: string | undefined,
): string | undefined {
  if (!slug) return slug;
  return slug.startsWith("org:") ? slug.slice("org:".length) : slug;
}
